import { action, observable } from 'mobx';
import copy from '@src/components/copy';
import Toast from '@src/components/toast';
import $$tracker from '@src/tracker';
import ActivityConfigStore from '@src/pages/shared/activity-config-store';
import useModal from '@src/utils/hooks/use-modal';
import useMount from '@src/utils/hooks/use-mount';
import ReservationModal, { ReservationModalStore } from './components/reservation-modal';
import ActivityRulesModal, { ActivityRulesModalStore } from './components/activity-rules-modal';
import Services from '@src/services/general';

class MainStore extends ActivityConfigStore {
  featureTopicUrl = '';
  @observable reservationHeroes = [];
  @observable teamId = this.getInvitationTeamId();
  @observable isTeamGuestMode = Boolean(this.teamId);
  @observable isGuestActivityEnded = false;
  @observable returnToGuestAfterReservation = false;
  @observable hasPurchasedTeamGift = false;
  @observable isOrdering = false;
  @observable teamInviteTinyUrl = '';
  @observable
  teamViewModel = {
    actionState: 'team_unavailable',
    teamId: '',
    isReserved: false
  };
  reservationModalStore = new ReservationModalStore({
    onSuccess: result => this.handleReservationSuccess(result),
    closeCallback: () => this.handleReservationSuccessClose()
  });
  activityRulesModalStore = new ActivityRulesModalStore();

  get displayAccount() {
    return this.loginState || this.account || '';
  }

  async handleReserveClick() {
    $$tracker.clickSubscribe();
    if (this.isReserved) {
      return false;
    }
    if (!this.loginState && !this.account) {
      this.login();
      return false;
    }
    try {
      const response = await this.$ztService.getPhone();
      this.reservationModalStore.showVerification(response && response.phone);
      this.reservationModal.open();
      return true;
    } catch (error) {
      const response = (error && error.serviceResponse) || error || {};
      if (Number(response.code) === -20101) {
        this.reservationModalStore.showPhoneBinding();
        this.reservationModal.open();
      } else {
        Toast.fail(response.message || response.msg || '获取账号信息失败', 1);
      }
      return false;
    }
  }

  async handleReservationSuccess(result) {
    let config;
    try {
      config = await this.$ztService.getConfig();
      this.setConfig(this.normalizeConfig(config));
    } catch (error) {
      config = { account: this.account, server_name: this.serverName };
    }
    window.__JXSJ_RESERVATION_CONFIG__ = config;
    this.reservationModalStore.showSuccess({ friends: result && result.friends });
    await this.loadReservationRanking();
    this.setReserved();
  }

  logout() {
    $$tracker.clickLogout(String(this.account || this.loginState || ''));
    return this.businessAccountInfo.logout();
  }

  openActivityRules() {
    $$tracker.clickActivityRule();
    this.activityRulesModalStore.setServerName(this.serverName);
    this.activityRulesModalStore.setBindGoldText(this.bindGoldText);
    this.activityRulesModal.open();
  }

  normalizeReservationHeroes(list) {
    if (!Array.isArray(list)) {
      return [];
    }
    return list
      .map((item, index) => {
        const serverName = item && typeof item.server === 'string' ? item.server.trim() : '';
        const roleName = item && typeof item.role === 'string' ? item.role.trim() : '';
        if (!serverName && !roleName) {
          return null;
        }
        return {
          id: `${serverName}-${roleName}-${index}`,
          displayText: `【${serverName || '新服名称'}】${roleName || '大侠'}`
        };
      })
      .filter(Boolean)
      .slice(0, 20);
  }

  @action.bound
  setReservationHeroes(list) {
    this.reservationHeroes = this.normalizeReservationHeroes(list);
  }

  async loadReservationRanking() {
    try {
      const response = await this.$ztService.showAccountList();
      this.setReservationHeroes(response && response.data);
    } catch (error) {
      this.setReservationHeroes([]);
    }
    return this.reservationHeroes;
  }

  @action.bound
  setReserved() {
    this.isReserved = true;
  }

  /**
   * 同时兼容 `?team_id=x#/` 与 `#/?team_id=x` 两种移动端邀请链接。
   * @returns {string} 邀请链接中的队伍 ID
   */
  getInvitationTeamId() {
    if (typeof window === 'undefined') {
      return '';
    }
    const searchTeamId = new URLSearchParams(window.location.search).get('team_id');
    const hashQuery = window.location.hash.split('?')[1] || '';
    return searchTeamId || new URLSearchParams(hashQuery).get('team_id') || '';
  }

  /**
   * 将 team_config 原始字段收敛为客态页使用的稳定状态。
   * @param {Object} team team_config 返回数据
   * @returns {{actionState: string, teamId: string, isReserved: boolean}} 客态页 ViewModel
   */
  normalizeTeam(team) {
    const source = team || {};
    const supportedStates = ['create_team', 'join_team', 'invite_friend', 'team_success', 'team_full', 'team_unavailable'];
    return {
      actionState: supportedStates.includes(source.button_status) ? source.button_status : 'team_unavailable',
      teamId: source.team_id || this.teamId,
      isReserved: Number(source.reserve_status) === 1
    };
  }

  /** @returns {Object} PC 同源业务状态对应的移动端客态视觉与可用操作 */
  get teamGuestViewModel() {
    const actionState = this.teamViewModel.actionState;
    const states = {
      team_success: 'success',
      team_full: 'unavailable',
      team_unavailable: 'pending'
    };
    const visualStates = {
      create_team: 'pending',
      invite_friend: 'success',
      team_success: 'success',
      team_full: 'unavailable',
      team_unavailable: 'pending'
    };
    const actions = {
      create_team: 'join',
      join_team: 'join',
      team_unavailable: 'join'
    };
    const labels = {
      create_team: '加入购买队伍',
      join_team: '加入购买队伍',
      invite_friend: '复制专属邀请链接',
      team_success: '组队购买成功',
      team_full: '队伍已满',
      team_unavailable: '加入购买队伍'
    };
    return {
      actionState,
      state: states[actionState] || 'pending',
      // 单主操作状态复用双按钮底图，避免三按钮底图留下重复文案。
      visualState: visualStates[actionState] || 'pending',
      primaryAction: actions[actionState] || '',
      primaryLabel: labels[actionState] || labels.team_unavailable,
      canPrimaryAction: ['create_team', 'join_team', 'invite_friend', 'team_unavailable'].includes(actionState) && !this.isOrdering,
      canJoin: ['create_team', 'join_team'].includes(actionState) && !this.isOrdering,
      canInvite: actionState === 'invite_friend' && !this.isOrdering,
      canCreate: ['create_team', 'join_team', 'team_full', 'team_unavailable'].includes(actionState) && !this.isOrdering,
      showPrimary: actionState !== 'invite_friend',
      showCreate: !['invite_friend', 'team_success'].includes(actionState),
      isReserved: this.teamViewModel.isReserved || this.isReserved,
      reserveLabel: this.teamViewModel.isReserved || this.isReserved ? '查看活动' : '立即预约新服'
    };
  }

  @action.bound
  setTeamViewModel(team) {
    this.teamViewModel = this.normalizeTeam(team);
  }

  @action.bound
  setGuestActivityEnded(isEnded) {
    this.isGuestActivityEnded = Boolean(isEnded);
  }

  @action.bound
  setTeamGiftPurchased(isPurchased) {
    this.hasPurchasedTeamGift = Boolean(isPurchased);
  }

  /** @returns {Promise<boolean>} `gift_list` 是否确认当前账号已购买组队礼包 */
  async loadTeamGiftStatus() {
    try {
      const response = await this.$ztService.giftList();
      const teamGift = Array.isArray(response && response.gift_list) && response.gift_list.find(item => Number(item && item.gift_id) === 1);
      this.setTeamGiftPurchased(Number(teamGift && teamGift.status) === 1);
    } catch (error) {
      this.setTeamGiftPurchased(false);
    }
    return this.hasPurchasedTeamGift;
  }

  /**
   * 使用邀请链接的 team_id 获取服务端权威队伍状态；失败时保持客态并禁用加入。
   * @returns {Promise<Object>} 当前客态页队伍 ViewModel
   */
  async loadTeamContext() {
    if (!this.teamId) {
      return this.teamViewModel;
    }
    try {
      const team = await this.$ztService.teamConfig(this.teamId);
      this.setGuestActivityEnded(false);
      const shouldShowPurchased = this.hasPurchasedTeamGift && team.button_status !== 'invite_friend';
      this.setTeamViewModel(shouldShowPurchased ? { ...team, button_status: 'team_success' } : team);
      this.prepareTeamInviteTinyUrl();
    } catch (error) {
      const response = (error && error.serviceResponse) || error || {};
      this.setGuestActivityEnded(Number(response.code) === -10154);
      // 活动结束时接口不再返回队伍状态，但客态仍需按设计稿保留加入与创建入口。
      this.setTeamViewModel({ button_status: Number(response.code) === -10154 ? 'join_team' : 'team_unavailable', team_id: this.teamId });
    }
    return this.teamViewModel;
  }

  /** @returns {Promise<Object>} 已购买状态优先的完整客态队伍状态 */
  async loadGuestTeamState() {
    if (!this.teamId) {
      return this.teamViewModel;
    }
    await this.loadTeamGiftStatus();
    return this.loadTeamContext();
  }

  /**
   * 客态页加入邀请队伍时创建移动支付订单，并跳转后端支付地址。
   * @param {string} teamId 邀请队伍 ID
   * @returns {Promise<boolean>} 是否成功创建订单并发起支付跳转
   */
  async createTeamOrder(teamId) {
    if (this.isOrdering) {
      return false;
    }
    if (!this.loginState && !this.account) {
      this.login();
      return false;
    }
    this.isOrdering = true;
    try {
      const result = await this.$ztService.createOrder(1, 'mobile', window.location.href, teamId || '');
      if (!result || !result.pay_url) {
        Toast.fail('支付地址获取失败，请稍后重试', 1);
        return false;
      }
      window.location.href = result.pay_url;
      return true;
    } catch (error) {
      const response = (error && error.serviceResponse) || error || {};
      if (Number(response.code) === -20109) {
        this.setTeamViewModel({
          button_status: 'team_success',
          reserve_status: this.teamViewModel.isReserved ? 1 : 0,
          team_id: this.teamViewModel.teamId || this.teamId
        });
        return false;
      }
      Toast.fail(response.message || response.msg || '创建订单失败，请稍后重试', 1);
      return false;
    } finally {
      this.isOrdering = false;
    }
  }

  /** @returns {Promise<boolean>|boolean} 是否进入邀请队伍支付 */
  handleGuestJoin() {
    $$tracker.clickJoinTeam(this.teamId || '');
    if (this.purchasePhase === 'closed' || this.isGuestActivityEnded) {
      Toast.info('活动已结束', 1);
      return false;
    }
    if (this.teamViewModel.actionState === 'team_unavailable') {
      Toast.info('队伍已失效', 1);
      return false;
    }
    if (!this.teamGuestViewModel.canJoin) {
      return false;
    }
    return this.createTeamOrder(this.teamId);
  }

  /** @returns {Promise<boolean>|boolean} 是否执行当前客态主操作 */
  handleGuestPrimaryAction() {
    if (this.teamGuestViewModel.primaryAction === 'create') {
      return this.handleGuestCreate();
    }
    if (this.teamGuestViewModel.primaryAction === 'join') {
      return this.handleGuestJoin();
    }
    if (this.teamGuestViewModel.primaryAction === 'invite') {
      return this.handleGuestInvite();
    }
    return false;
  }

  /** @returns {boolean} 是否进入主预约页；客态创建入口不下单 */
  handleGuestCreate() {
    $$tracker.clickCreateTeamLink(this.teamId || '');
    return this.navigateToMainReservation();
  }

  buildTeamInviteUrl(teamId = '') {
    if (!teamId || typeof window === 'undefined') {
      return '';
    }
    // 后端只返回 team_id，使用当前部署地址可自动适配测试与正式环境。
    const link = document.createElement('a');
    link.href = window.location.href;
    link.search = `?team_id=${encodeURIComponent(teamId)}`;
    link.hash = '/';
    return link.href;
  }

  async prepareTeamInviteTinyUrl() {
    if (this.teamViewModel.actionState !== 'invite_friend') {
      this.teamInviteTinyUrl = '';
      return '';
    }
    const inviteUrl = this.buildTeamInviteUrl(this.teamViewModel.teamId || this.teamId);
    if (!inviteUrl) {
      return '';
    }
    try {
      const response = await Services.createTinyUrl(inviteUrl);
      this.teamInviteTinyUrl = (response && response.tiny_url) || '';
    } catch (error) {
      this.teamInviteTinyUrl = '';
    }
    return this.teamInviteTinyUrl;
  }

  /** @returns {Promise<boolean>|boolean} 是否成功复制当前队伍的邀请链接 */
  async handleGuestInvite() {
    if (!this.teamGuestViewModel.canInvite) {
      return false;
    }
    $$tracker.clickUrlBuy();
    const inviteUrl = this.buildTeamInviteUrl(this.teamViewModel.teamId || this.teamId);
    if (!inviteUrl) {
      Toast.info('邀请链接暂不可用', 1);
      return false;
    }
    const shareText = `我已预约《剑侠世界》首个生态服，快和我组队购买钜惠礼包，链接：${this.teamInviteTinyUrl || inviteUrl}，一起玩新服这波福利不能错过！`;
    copy(shareText, { autoDisplaySupportedAlert: false, autoDisplayUnsupportedAlert: false });
    return copy(shareText, {
      showText: false,
      successPreText: '邀请链接已复制',
      successToastDuration: 1000
    }).isSupport;
  }

  /**
   * 按 PC 客态协议退出客态并定位预约入口，不自动替用户提交预约。
   * @returns {boolean} 是否完成页面切换
   */
  handleGuestReserveAction() {
    $$tracker.clickSubscribeLink(this.teamId || '');
    return this.navigateToMainReservation();
  }

  // 两个跳转入口共用页面切换，不触发登录、预约提交或支付；各自保留原点击埋点。
  navigateToMainReservation() {
    this.returnToGuestAfterReservation = Boolean(this.teamId) && !this.teamGuestViewModel.isReserved;
    this.isTeamGuestMode = false;
    setTimeout(() => {
      const reservationAction = document.getElementById('reservation-action');
      reservationAction && reservationAction.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 0);
    return true;
  }

  @action.bound
  handleReservationSuccessClose() {
    if (!this.returnToGuestAfterReservation || !this.teamId) {
      return false;
    }
    this.returnToGuestAfterReservation = false;
    this.isTeamGuestMode = true;
    return true;
  }

  /**
   * 资料片地址未配置时沿用 PC 端提示；配置后在新窗口打开。
   * @returns {void}
   */
  onFeatureTopicClick() {
    $$tracker.clickGoSpecial();
    if (!this.featureTopicUrl) {
      Toast.info('敬请期待', 0.6);
      return;
    }
    window.open(this.featureTopicUrl, '_blank', 'noopener,noreferrer');
  }

  onOfficialSiteClick() {
    $$tracker.clickGoGuanwang();
  }

  use() {
    super.use();
    this.reservationModal = useModal(ReservationModal, { store: this.reservationModalStore });
    this.activityRulesModal = useModal(ActivityRulesModal, { store: this.activityRulesModalStore });
    useMount(() => {
      this.loadReservationRanking();
      this.loadGuestTeamState();
    });
  }
}

export default MainStore;
